/**
 *
 * The env duck. This is where information about the browsing environment is stored.
 * Default export is the reducer.
 *
 * Action Types:
 * - BROWSER_RESIZED
 * - SET_COUNTRY_CODE_AND_CURRENCY
 * - UPDATE_COUNTRY_CODE_AND_CURRENCY
 * - SET_USER_AGENT
 * - SET_IOS_DEVICE
 * - LOCALE_REQUESTED
 * - CAN_PLAY_VIDEO
 * - UPDATED_COUNTRY_CODE_SUCCEEDED
 * - UPDATED_COUNTRY_CODE_FAILED
 *
 * Action Creators:
 * - browserResized
 * - requestLocale
 * - setUserAgent
 * - setIosDevice
 * - canPlayVideo
 * - setCountryCodeAndCurrency
 * - updateCountryCodeAndCurrency
 * - updatedCountryCodeSucceeded
 * - updatedCountryCodeFailed
 */
import get from 'lodash/get';

import type {
  BrowserResized,
  EnvAction,
  EnvState,
  SetUserAgent,
  SetIOSDevice,
  CanPlayVideo,
  SetCountryCodeAndCurrency,
  UpdateCountryCodeAndCurrency,
  UpdatedCountryCodeSucceeded,
  UpdatedCountryCodeFailed,
} from '../types';

import { isMobile, layoutSize } from '../styling/mixins/responsive';

/** @type {ActionType} */
export const BROWSER_RESIZED = 'PB/env/BROWSER_RESIZED';

/** @type {ActionType} */
export const SET_COUNTRY_CODE_AND_CURRENCY =
  'PB/env/SET_COUNTRY_CODE_AND_CURRENCY';

/** @type {ActionType} */
export const UPDATE_COUNTRY_CODE_AND_CURRENCY =
  'PB/env/UPDATE_COUNTRY_CODE_AND_CURRENCY';

/** @type {ActionType} */
export const UPDATED_COUNTRY_CODE_SUCCEEDED =
  'PB/env/UPDATE_COUNTRY_CODE_SUCCEEDED';

/** @type {ActionType} */
export const UPDATED_COUNTRY_CODE_FAILED = 'PB/env/UPDATE_COUNTRY_CODE_FAILED';

/** @type {ActionType} */
export const SET_USER_AGENT = 'PB/env/SET_USER_AGENT';

/** @type {ActionType} */
export const SET_IOS_DEVICE = 'PB/env/SET_IOS_DEVICE';

/** @type {ActionType} */
export const CAN_PLAY_VIDEO = 'PB/env/CAN_PLAY_VIDEO';

/** @type {ActionType} */
export const LOCALE_REQUESTED = 'PB/env/LOCALE_REQUESTED';

export const DEFAULT_MOBILE_SIZE = {
  height: 667,
  width: 375,
};

export const DEFAULT_DESKTOP_SIZE = {
  height: 768,
  width: 1024,
};

export const initialState = {
  countryCode: 'US',
  preferredCountryCode: 'US',
  currency: 'USD',
  preferredCurrency: 'USD',
  isIOSDevice: false,
  isMobile: false,
  canPlayVideo: 'unknown',
  layoutSize: 'medium',
  windowWidth: DEFAULT_DESKTOP_SIZE.width,
  userAgent: '',
};

export default function env(
  state: EnvState = initialState,
  action: EnvAction,
): EnvState {
  switch (action.type) {
    case BROWSER_RESIZED:
      const browserWidth = get(action, 'payload.dimensions.width', false);
      if (browserWidth) {
        return {
          ...state,
          windowWidth: browserWidth,
          layoutSize: layoutSize(browserWidth),
          isMobile: isMobile(browserWidth),
        };
      }
      break;

    case UPDATE_COUNTRY_CODE_AND_CURRENCY:
      const updatedCountryCode = get(
        action,
        'payload.preferredCountryCode',
        state.countryCode,
      );
      const updatedCurrency = get(
        action,
        'payload.preferredCurrency',
        state.currency,
      );
      return {
        ...state,
        preferredCountryCode: updatedCountryCode,
        preferredCurrency: updatedCurrency,
      };

    case SET_COUNTRY_CODE_AND_CURRENCY:
      const countryCode = get(action, 'payload.countryCode', state.countryCode);
      const preferredCountryCode = get(
        action,
        'payload.preferredCountryCode',
        state.preferredCountryCode,
      );

      const currency = get(action, 'payload.currency', state.currency);
      const preferredCurrency = get(
        action,
        'payload.preferredCurrency',
        state.preferredCurrency,
      );
      return {
        ...state,
        countryCode,
        preferredCountryCode,
        currency,
        preferredCurrency,
      };

    case SET_USER_AGENT:
      const userAgent = get(action, 'payload.userAgent', state.userAgent);
      return { ...state, userAgent };

    case SET_IOS_DEVICE:
      const isIOSDevice = get(action, 'payload.isIOSDevice', state.isIOSDevice);
      return { ...state, isIOSDevice };

    case CAN_PLAY_VIDEO:
      const currentValue = state.canPlayVideo;
      const receivedValue = get(action, 'payload.canPlayVideo', currentValue);

      // Default to the current value
      let canPlayVideoValue = currentValue;
      // If we recieve a truthy value, then trust it...
      // OR
      // If we receive a positive value, and we're currently negative, then trust it
      if (currentValue === 'unknown' || (receivedValue && !currentValue)) {
        canPlayVideoValue = receivedValue;
      }
      return { ...state, canPlayVideo: canPlayVideoValue };

    default:
      return state;
  }
}

export function browserResized(dimensions: {
  width: number,
  height: number,
}): BrowserResized {
  return {
    type: BROWSER_RESIZED,
    payload: { dimensions },
  };
}

export function requestLocale() {
  return {
    type: LOCALE_REQUESTED,
  };
}

export function setCountryCodeAndCurrency(
  countryCode: string,
  preferredCountryCode: string,
  currency: string,
  preferredCurrency: string,
): SetCountryCodeAndCurrency {
  return {
    type: SET_COUNTRY_CODE_AND_CURRENCY,
    payload: { countryCode, preferredCountryCode, currency, preferredCurrency },
  };
}

export function updateCountryCodeAndCurrency(
  preferredCountryCode: string,
  preferredCurrency: string,
): UpdateCountryCodeAndCurrency {
  return {
    type: UPDATE_COUNTRY_CODE_AND_CURRENCY,
    payload: {
      preferredCurrency,
      preferredCountryCode,
    },
  };
}

export function updatedCountryCodeSucceeded(): UpdatedCountryCodeSucceeded {
  return {
    type: UPDATED_COUNTRY_CODE_SUCCEEDED,
  };
}

export function updatedCountryCodeFailed(
  message: string,
): UpdatedCountryCodeFailed {
  return {
    type: UPDATED_COUNTRY_CODE_FAILED,
    payload: {
      message,
    },
  };
}

export function setUserAgent(userAgent: string): SetUserAgent {
  return {
    type: SET_USER_AGENT,
    payload: { userAgent },
  };
}

export function setIOSDevice(isIOSDevice: boolean): SetIOSDevice {
  return {
    type: SET_IOS_DEVICE,
    payload: { isIOSDevice },
  };
}

export function canPlayVideo(canPlayVideo: boolean): CanPlayVideo {
  return {
    type: CAN_PLAY_VIDEO,
    payload: { canPlayVideo },
  };
}
